import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FilterListSMSIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/base-components';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { isTCAPIConversationLoaded } from '@rsos/reduxDataStore/src/features/conversations/conversations.typings';
import { useCallBackTCAPIConversationMutation } from '@rsos/reduxDataStore/src/features/conversations/tcapiAPI';
import {
  useGetConversationIsLoading,
  useGetConversation,
  useGetCreateConversation,
  useGetEndConversation,
  useHasConversationButtons,
  useIsConversationEnded,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import useIsVT911Enabled from '../../../../hooks/useIsVT911Enabled';
import { formatDateWithLocalTimezone } from '../../../../utils/dateUtilities';
import ConditionalFeatureDisplay from '../../../Common/ConditionalFeatureDisplay/ConditionalFeatureDisplay';
import { useChatContext } from '../ChatContext';
import { CollapseButton } from '../SharedChatUI/CollapseButton';
import { TranslationMenu } from '../SharedChatUI/TranslationMenu';
import { TranslationMenuButton } from '../SharedChatUI/TranslationMenuButton';
import { LiveVideoButtonContainer } from '../VideoTo911/LiveVideoButtonContainer';
import { useEndingMessage } from '../useEndingMessage';
import {
  HeaderContainer,
  RedCircle,
  StartChatButton,
  StartChatContainer,
  IconButtonsBox,
} from './ConversationsChatbox.styles';
import EndChatModal from './EndChatModal';
import TransferButton from './TransferToAgency/TransferButton';
import TransferToAgencyModal from './TransferToAgency/TransferToAgencyModal';
import { canCallBackSession, getChatButtonText } from './helpers';

interface ConversationsChatHeaderProps {
  handleStartChat?: () => void;
  isForOverview?: boolean;
  isConversationStarted: boolean;
}

const CALL_BACK_FAILURE_FALLBACK =
  'Could not restart this chat. Please try again.';

/**
 * What a dispatcher reads when a call-back does not go through.
 *
 * Both refusals are 409 and differ only in the body, so the status cannot
 * pick the sentence. The server names the reason with a stable code and this
 * side owns the wording. The too-old sentence doubles as the explanation for
 * the disabled button, which has no other way to say why. A reason this build
 * does not know, and a failure carrying no reason at all, both read as the
 * generic line.
 */
const CALL_BACK_REFUSED_SESSION_TOO_OLD = 'session_too_old';

const CALL_BACK_FAILURE_MESSAGES: Record<string, string> = {
  [CALL_BACK_REFUSED_SESSION_TOO_OLD]: 'This chat is too old to restart.',
  call_back_in_progress:
    'A new chat is already starting for this conversation.',
};

const getCallBackErrorMessage = (error: unknown): string => {
  const detail =
    typeof error === 'object' && error !== null && 'data' in error
      ? (error as { data?: { detail?: { code?: unknown } } }).data?.detail
      : undefined;
  const code =
    typeof detail === 'object' && detail !== null ? detail.code : undefined;

  return (
    (typeof code === 'string' && CALL_BACK_FAILURE_MESSAGES[code]) ||
    CALL_BACK_FAILURE_FALLBACK
  );
};

const ConversationsChatHeader = ({
  isConversationStarted,
  handleStartChat,
  isForOverview = false,
}: ConversationsChatHeaderProps) => {
  const { collapse } = useChatContext();
  const isVT911Enabled = useIsVT911Enabled();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const orgName = useCurrentOrgName();

  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const hasButtons = useHasConversationButtons(callerID);
  const isConversationPotentiallyEnded = useIsConversationEnded(callerID);
  const isConversationLoading = useGetConversationIsLoading(callerID);
  const conversation = useGetConversation(callerID);
  const isConversationEnded =
    isConversationPotentiallyEnded && isConversationStarted;
  const isConversationOngoing =
    !isConversationPotentiallyEnded && isConversationStarted;

  // InDigital does not support ending an initiated conference.
  const isEndBlockedByPendingTransfer =
    isConversationOngoing && conversation?.transfer?.status === 'pending';

  const [fetchCreateConversation, createConversationStates] =
    useGetCreateConversation(callerID);

  const [fetchEndConversation, endConversationStates] =
    useGetEndConversation(callerID);

  const [triggerCallBack, callBackResult] =
    useCallBackTCAPIConversationMutation();

  const getEndingMessage = useEndingMessage({
    orgName: orgName ?? '',
    callerID: callerID ?? undefined,
  });

  const displayPhoneNumber = formatInternationalPhoneNumberHuman(
    callerID ?? '',
  );

  const displayStartTime =
    conversation &&
    isTCAPIConversationLoaded(conversation) &&
    conversation.data.creation_time
      ? formatDateWithLocalTimezone(
          conversation.data.creation_time,
          'YYYY-MM-DD HH:mm',
        )
      : null;

  // Re-engages an ended TCAPI session over the native channel.
  const isCallBackOffered =
    isConversationPotentiallyEnded && conversation?.source === SOURCE_TCAPI;
  // Disabled, not hidden: it is the only control left in a closed TCAPI header.
  const isCallBackExpired =
    isCallBackOffered && !canCallBackSession(conversation);
  // Kept with the caller it was raised for. The mutation result belongs to
  // this component instance rather than to a caller, carries no record of its
  // own arguments, and the header is never remounted when the dispatcher
  // selects another call — so a refusal would otherwise render under the next
  // chat they open.
  const [callBackFailure, setCallBackFailure] = useState<{
    callerID: string;
    message: string;
  } | null>(null);
  // A disabled button fires no hover events, so its greyed-out state is the
  // only thing a dispatcher gets on a chat past the window. Say why in the
  // same slot a refused call-back uses, from the same sentence, so the two
  // cannot drift apart.
  const callBackError =
    callBackFailure && callBackFailure.callerID === callerID
      ? callBackFailure.message
      : isCallBackExpired
        ? CALL_BACK_FAILURE_MESSAGES[CALL_BACK_REFUSED_SESSION_TOO_OLD]
        : null;

  // Drops a request left in flight when the dispatcher moves to another call,
  // so its spinner does not appear over the new one. Unconditional on purpose,
  // including the first run: there is nothing to keep from a previous caller.
  const { reset: resetCallBack } = callBackResult;
  useEffect(() => {
    resetCallBack();
  }, [callerID, resetCallBack]);

  const handleCallBack = async () => {
    const result = await triggerCallBack({ callerID, orgName });
    if ('error' in result) {
      setCallBackFailure({
        callerID: callerID ?? '',
        message: getCallBackErrorMessage(result.error),
      });
      return;
    }

    setCallBackFailure(null);
    handleStartChat?.();
    collapse?.(false);
  };

  const handleStartConversation = async () => {
    if (fetchCreateConversation) {
      return fetchCreateConversation({ callerID, orgName }).then(() => {
        handleStartChat?.();
        collapse?.(false);
        return createConversationStates;
      });
    } else {
      return { isSuccess: false };
    }
  };

  const onStartConversation = async () => {
    if (isConversationOngoing) {
      return { isSuccess: true };
    }

    return handleStartConversation();
  };

  const handleDefaultStartClick = () => {
    if (isConversationOngoing) {
      setIsModalOpen(true);
    } else {
      handleStartConversation();
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalConfirm = async () => {
    if (fetchEndConversation) {
      const ending = await getEndingMessage();
      fetchEndConversation({
        callerID: callerID ?? '',
        orgName: orgName ?? '',
        endMessage: ending?.body,
        doNotTranslate: ending?.doNotTranslate,
      });
    }

    setIsModalOpen(false);
  };

  return (
    <HeaderContainer data-name="conversations-chat-header">
      <Box horizontal justify="space-between" width="100%">
        <Box>
          <Text inline fontWeight="bold">
            <Text color="primaryText" size="normal">
              {isConversationOngoing && (
                <RedCircle data-name="conversations-live-indicator" />
              )}
              {isConversationEnded ? 'Closed Chat' : 'Live Chat'}
            </Text>
            <Box horizontal>
              <Text color="secondaryText" fontWeight="regular" size="xsmall">
                {displayPhoneNumber}
              </Text>
              {displayStartTime && (
                <Text color="secondaryText" fontWeight="regular" size="xsmall">
                  &nbsp;&middot; Started {displayStartTime}
                </Text>
              )}
            </Box>
          </Text>
        </Box>
        {isConversationOngoing && !isForOverview && (
          <IconButtonsBox>
            <TranslationMenuButton />
            <CollapseButton />
          </IconButtonsBox>
        )}
      </Box>

      {isConversationOngoing && !isForOverview && <TranslationMenu />}

      {hasButtons && (
        <StartChatContainer gridColumnGap="8px">
          {isVT911Enabled && (
            <Box width="50%">
              <LiveVideoButtonContainer
                onStartConversation={onStartConversation}
              />
            </Box>
          )}
          <ConditionalFeatureDisplay
            conditions={[
              { featureFlagEnabled: 'tt911-transfer-to-agency' },
              {
                showIf: conversation?.source === SOURCE_TCAPI,
                reason: 'source-is-not-tcapi',
              },
            ]}
            dataName="transfer-to-agency-button"
          >
            {isConversationOngoing && (
              <div style={{ width: '50%' }}>
                <TransferButton onClick={() => setIsTransferModalOpen(true)} />
              </div>
            )}
          </ConditionalFeatureDisplay>
          <Box
            alignItems="center"
            justifyContent="center"
            horizontal
            width={isVT911Enabled ? '50%' : '100%'}
          >
            <StartChatButton
              onClick={handleDefaultStartClick}
              disabled={isEndBlockedByPendingTransfer}
              title={
                isEndBlockedByPendingTransfer
                  ? 'A call cannot be ended while a transfer is pending. Wait for the transfer to be accepted or timeout.'
                  : undefined
              }
              btnType={isConversationOngoing ? 'warning_tertiary' : 'tertiary'}
              isLoadingProp={
                createConversationStates?.isLoading ||
                endConversationStates?.isLoading ||
                isConversationLoading
              }
            >
              <FilterListSMSIcon width={12} height={12} />
              {getChatButtonText({
                conversation,
                isConversationOngoing,
              })}
            </StartChatButton>
          </Box>
        </StartChatContainer>
      )}
      {isCallBackOffered && (
        <StartChatContainer gridColumnGap="8px">
          <StartChatButton
            dataName="tcapi-call-back-button"
            onClick={handleCallBack}
            disabled={isCallBackExpired}
            btnType="tertiary"
            isLoadingProp={callBackResult?.isLoading}
          >
            <FilterListSMSIcon width={12} height={12} />
            Start New Chat
          </StartChatButton>
        </StartChatContainer>
      )}
      {isCallBackOffered && callBackError && (
        <Text
          data-name="tcapi-call-back-error"
          size="small"
          color="heavyDanger"
        >
          {callBackError}
        </Text>
      )}
      <EndChatModal
        isLoading={endConversationStates?.isLoading}
        isOpened={isModalOpen}
        callerID={callerID}
        conversation={conversation}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
      />
      <TransferToAgencyModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        callerID={callerID}
        orgName={orgName}
      />
    </HeaderContainer>
  );
};

export default ConversationsChatHeader;
